/**
 * API Service for Netlify Functions
 * 
 * Replaces direct Firebase Admin SDK calls with HTTP API calls to Netlify Functions.
 * This ensures Firebase credentials remain secure on the server side.
 */

// API endpoint configuration
const API_BASE = '/.netlify/functions';

// HTTP client with error handling
class ApiClient {
  private async request(endpoint: string, options: RequestInit = {}): Promise<unknown> {
    const url = `${API_BASE}${endpoint}`;
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async post(endpoint: string, data: unknown): Promise<unknown> {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async get(endpoint: string): Promise<unknown> {
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async put(endpoint: string, data: unknown): Promise<unknown> {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch(endpoint: string, data: unknown): Promise<unknown> {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }
}

const apiClient = new ApiClient();

// Type definitions for API responses
export interface ApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  data?: T;
}

export interface SnapshotResponse extends ApiResponse {
  id: string;
}

export interface GetSnapshotResponse extends ApiResponse {
  snapshot: UserSnapshotApi;
}

// API-compatible user snapshot interface (with serialized timestamps)
export interface UserSnapshotApi {
  id: string;
  timestamp: string; // ISO string instead of Firestore Timestamp
  sessionId: string;
  userData: {
    maskSizing: unknown | null;
    equipoSeleccionado: unknown | null;
    mascarillaSeleccionada: unknown | null;
    bunoIncluido: boolean;
    bundleInfo?: unknown;
  };
  metadata: {
    userAgent?: string;
    ipAddress?: string;
    stage: 'productos' | 'plan-seleccion' | 'carrito';
    completedSteps: string[];
    breathingSelfReport?: string;
    noseScore?: number;
    maskRecommendation?: string;
  };
  expiresAt: string; // ISO string instead of Firestore Timestamp
  status: 'active' | 'expired' | 'used';
  version: string;
  environment: string;
  updatedAt?: string; // ISO string
  usedAt?: string; // ISO string
}

// Snapshot creation payload
export interface CreateSnapshotPayload {
  sessionId: string;
  userData: UserSnapshotApi['userData'];
  metadata: UserSnapshotApi['metadata'];
  expiresAt: string; // ISO string
  status: 'active';
}

// API functions that mirror the original Firebase functions

/**
 * Save user snapshot to Firebase via Netlify Function
 */
export async function saveUserSnapshot(snapshotData: CreateSnapshotPayload): Promise<string> {
  try {
    const response = await apiClient.post('/create-snapshot', snapshotData);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to create snapshot');
    }
    
    console.log(`📝 User snapshot saved via API: ${response.id}`);
    return response.id;
    
  } catch (error) {
    console.error('API - Failed to save user snapshot:', error);
    throw new Error(`Failed to save user snapshot: ${error}`);
  }
}

/**
 * Get user snapshot by ID via Netlify Function
 */
export async function getUserSnapshot(snapshotId: string): Promise<UserSnapshotApi | null> {
  try {
    const response = await apiClient.get(`/get-snapshot/${snapshotId}`);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to get snapshot');
    }
    
    console.log(`📖 User snapshot retrieved via API: ${snapshotId}`);
    return response.snapshot;
    
  } catch (error) {
    // Handle 404 and 410 (expired) as null return, not errors
    if (error instanceof Error) {
      if (error.message.includes('404') || error.message.includes('not found')) {
        console.log(`📖 Snapshot not found: ${snapshotId}`);
        return null;
      }
      if (error.message.includes('410') || error.message.includes('expired')) {
        console.log(`⏰ Snapshot expired: ${snapshotId}`);
        return null;
      }
    }
    
    console.error('API - Failed to get user snapshot:', error);
    throw new Error(`Failed to get user snapshot: ${error}`);
  }
}

/**
 * Update user snapshot via Netlify Function
 */
export async function updateUserSnapshot(
  snapshotId: string,
  updates: Partial<UserSnapshotApi>
): Promise<void> {
  try {
    const response = await apiClient.put(`/update-snapshot/${snapshotId}`, updates);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to update snapshot');
    }
    
    console.log(`📝 User snapshot updated via API: ${snapshotId}`);
    
  } catch (error) {
    console.error('API - Failed to update user snapshot:', error);
    throw new Error(`Failed to update user snapshot: ${error}`);
  }
}

/**
 * Mark snapshot as used via Netlify Function
 */
export async function markSnapshotAsUsed(snapshotId: string): Promise<void> {
  try {
    const response = await apiClient.post('/mark-snapshot-used', { id: snapshotId });
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to mark snapshot as used');
    }
    
    console.log(`✅ Snapshot marked as used via API: ${snapshotId}`);
    
  } catch (error) {
    console.error('API - Failed to mark snapshot as used:', error);
    throw new Error(`Failed to mark snapshot as used: ${error}`);
  }
}

/**
 * Cleanup expired snapshots via Netlify Function
 */
export async function cleanupExpiredSnapshots(): Promise<number> {
  try {
    const response = await apiClient.post('/cleanup-snapshots', {});
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to cleanup expired snapshots');
    }
    
    const count = response.cleanedUp || 0;
    console.log(`🧹 Cleaned up ${count} expired snapshots via API`);
    return count;
    
  } catch (error) {
    console.error('API - Failed to cleanup expired snapshots:', error);
    throw new Error(`Failed to cleanup expired snapshots: ${error}`);
  }
}

/**
 * Create Firestore timestamp equivalent for API
 */
export function createTimestamp(date?: Date): string {
  return (date || new Date()).toISOString();
}

/**
 * Convert API timestamp string to Date
 */
export function timestampToDate(timestamp: string): Date {
  return new Date(timestamp);
}

/**
 * Health check for API functions
 */
export async function checkApiHealth(): Promise<boolean> {
  try {
    // Try to cleanup (which is a safe read-only-ish operation)
    await cleanupExpiredSnapshots();
    return true;
  } catch (error) {
    console.error('API health check failed:', error);
    return false;
  }
}

// Export types for use in other files
export type { UserSnapshotApi as UserSnapshot, CreateSnapshotPayload };